import { Check, ListFilter, X } from 'lucide-react';
import { type ComponentType, type ReactNode, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { cn } from '@apps/web/lib/utils';

// Generic, dimension-driven filter UI modeled on the admin Agent Traces filter
// bar. A filter menu lists the available dimensions; drilling into one opens a
// searchable, multi-select option list with per-option counts. Active
// selections render as removable chips. Values are opaque strings, so callers
// own the mapping to their own state (e.g. office IDs).

export type FilterOption = {
    readonly value: string;
    readonly label: string;
    readonly count?: number;
};

export type FilterDimension = {
    readonly key: string;
    readonly label: string;
    readonly icon: ComponentType<{ readonly className?: string }>;
    readonly options: ReadonlyArray<FilterOption>;
    readonly multiSelect: boolean;
    readonly searchable?: boolean;
};

export type FilterChip = {
    readonly dimension: string;
    readonly values: ReadonlyArray<string>;
};

/**
 * Toggles a value within the chip for the given dimension. Single-select
 * dimensions replace their value; multi-select dimensions toggle membership
 * and drop the chip when the last value is removed.
 */
export function toggleChipValue(
    chips: ReadonlyArray<FilterChip>,
    dimension: FilterDimension,
    value: string,
): Array<FilterChip> {
    const existing = chips.find((chip) => chip.dimension === dimension.key);
    if (!existing) {
        return [...chips, { dimension: dimension.key, values: [value] }];
    }
    if (!dimension.multiSelect) {
        return chips.map((chip) =>
            chip === existing ? { ...chip, values: [value] } : chip,
        );
    }
    const nextValues = existing.values.includes(value)
        ? existing.values.filter((existingValue) => existingValue !== value)
        : [...existing.values, value];
    if (nextValues.length === 0) {
        return chips.filter((chip) => chip !== existing);
    }
    return chips.map((chip) =>
        chip === existing ? { ...chip, values: nextValues } : chip,
    );
}

function FilterOptionList({
    dimension,
    selectedValues,
    disabled,
    onToggle,
}: {
    readonly dimension: FilterDimension;
    readonly selectedValues: ReadonlyArray<string>;
    readonly disabled: boolean;
    readonly onToggle: (value: string) => void;
}) {
    return (
        <Command>
            {dimension.searchable ? (
                <CommandInput
                    placeholder={`Search ${dimension.label.toLowerCase()}…`}
                />
            ) : null}
            <CommandList>
                <CommandEmpty>No options found.</CommandEmpty>
                <CommandGroup>
                    {dimension.options.map((option) => {
                        const isSelected = selectedValues.includes(
                            option.value,
                        );
                        return (
                            <CommandItem
                                key={option.value}
                                value={`${option.label} ${option.value}`}
                                disabled={disabled}
                                onSelect={() => onToggle(option.value)}
                            >
                                <Check
                                    className={cn(
                                        'mr-2 h-4 w-4 shrink-0',
                                        isSelected
                                            ? 'opacity-100'
                                            : 'opacity-0',
                                    )}
                                />
                                <span className="min-w-0 flex-1 truncate">
                                    {option.label}
                                </span>
                                {option.count !== undefined ? (
                                    <Badge variant="secondary" className="ml-2">
                                        {option.count}
                                    </Badge>
                                ) : null}
                            </CommandItem>
                        );
                    })}
                </CommandGroup>
            </CommandList>
        </Command>
    );
}

/**
 * The `+` trigger that opens the list of available dimensions. Drilling into a
 * dimension reveals its searchable, multi-select option list.
 */
export function FilterAddMenu({
    dimensions,
    chips,
    disabled = false,
    onChange,
    label,
    className,
    extraItems,
}: {
    readonly dimensions: ReadonlyArray<FilterDimension>;
    readonly chips: ReadonlyArray<FilterChip>;
    readonly disabled?: boolean;
    readonly onChange: (chips: ReadonlyArray<FilterChip>) => void;
    readonly label?: string;
    readonly className?: string;
    readonly extraItems?: ReactNode;
}) {
    const [open, setOpen] = useState(false);
    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    variant="outline"
                    size={label ? 'sm' : 'icon'}
                    className={cn(
                        label
                            ? 'h-8 gap-1.5 rounded-full border-transparent px-3'
                            : 'h-9 w-9 shrink-0',
                        className,
                    )}
                    aria-label={label ?? 'Add filter'}
                    disabled={disabled}
                >
                    <ListFilter className="h-4 w-4" />
                    {label ? <span>{label}</span> : null}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
                {dimensions.map((dimension) => {
                    const chip = chips.find(
                        (candidate) => candidate.dimension === dimension.key,
                    );
                    const Icon = dimension.icon;
                    return (
                        <DropdownMenuSub key={dimension.key}>
                            <DropdownMenuSubTrigger>
                                <Icon className="text-muted-foreground mr-2 h-4 w-4" />
                                <span>{dimension.label}</span>
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="w-64 p-0">
                                <FilterOptionList
                                    dimension={dimension}
                                    selectedValues={chip?.values ?? []}
                                    disabled={disabled}
                                    onToggle={(value) => {
                                        onChange(
                                            toggleChipValue(
                                                chips,
                                                dimension,
                                                value,
                                            ),
                                        );
                                        if (!dimension.multiSelect) {
                                            setOpen(false);
                                        }
                                    }}
                                />
                            </DropdownMenuSubContent>
                        </DropdownMenuSub>
                    );
                })}
                {extraItems}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function FilterChipPill({
    dimension,
    chip,
    disabled,
    onToggleValue,
    onRemove,
}: {
    readonly dimension: FilterDimension;
    readonly chip: FilterChip;
    readonly disabled: boolean;
    readonly onToggleValue: (value: string) => void;
    readonly onRemove: () => void;
}) {
    const [open, setOpen] = useState(false);
    const Icon = dimension.icon;
    const [firstValue] = chip.values;
    const valueSummary =
        chip.values.length === 1
            ? (dimension.options.find((option) => option.value === firstValue)
                  ?.label ?? firstValue)
            : `${chip.values.length} selected`;

    return (
        <div className="bg-background flex h-8 items-center overflow-hidden rounded-md border text-xs">
            <span className="text-muted-foreground flex items-center gap-1.5 pr-1 pl-2.5">
                <Icon className="h-3.5 w-3.5" />
                {dimension.label}
            </span>
            <span className="text-muted-foreground px-1">is</span>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild={true}>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 max-w-[200px] rounded-none px-1.5 text-xs font-medium"
                    >
                        <span className="truncate">{valueSummary}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-0">
                    <FilterOptionList
                        dimension={dimension}
                        selectedValues={chip.values}
                        disabled={disabled}
                        onToggle={(value) => {
                            onToggleValue(value);
                            if (!dimension.multiSelect) {
                                setOpen(false);
                            }
                        }}
                    />
                </PopoverContent>
            </Popover>
            <Button
                variant="ghost"
                size="sm"
                className="h-8 rounded-none px-1.5"
                aria-label={`Remove ${dimension.label} filter`}
                disabled={disabled}
                onClick={onRemove}
            >
                <X className="h-3 w-3" />
            </Button>
        </div>
    );
}

/**
 * Renders the active filter chips with a "Clear filters" affordance. Returns
 * nothing when no filters are active.
 */
export function ActiveFilterChips({
    dimensions,
    chips,
    disabled = false,
    onChange,
}: {
    readonly dimensions: ReadonlyArray<FilterDimension>;
    readonly chips: ReadonlyArray<FilterChip>;
    readonly disabled?: boolean;
    readonly onChange: (chips: ReadonlyArray<FilterChip>) => void;
}) {
    if (chips.length === 0) {
        return null;
    }
    return (
        <div className="flex flex-wrap items-center gap-2">
            {chips.map((chip) => {
                const dimension = dimensions.find(
                    (candidate) => candidate.key === chip.dimension,
                );
                if (!dimension) {
                    return null;
                }
                return (
                    <FilterChipPill
                        key={chip.dimension}
                        dimension={dimension}
                        chip={chip}
                        disabled={disabled}
                        onToggleValue={(value) =>
                            onChange(toggleChipValue(chips, dimension, value))
                        }
                        onRemove={() =>
                            onChange(
                                chips.filter(
                                    (candidate) =>
                                        candidate.dimension !== chip.dimension,
                                ),
                            )
                        }
                    />
                );
            })}
            <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground h-8 text-xs"
                disabled={disabled}
                onClick={() => onChange([])}
            >
                Clear filters
            </Button>
        </div>
    );
}
